/**
 * Where new frames land on the old canvas (Part 06 Task 7): beside their
 * source frame, where Tidy would put the newest frames, or in a row from a
 * point. Moved from the call sites that placed frames themselves; Phase 7
 * deletes it with the canvas.
 */
import type { Node } from "@xyflow/react";
import type { FrameType } from "@/api/spaces";
import { tidyFramesByTimePage } from "@/components/layout/tidy-frames";
import type { FrameData } from "@/lib/frame-data";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import { nextFramePosition } from "@/lib/frame-placement";
import type {
  FramePlacementRequest,
  FrameSize,
  WorkspacePoint,
} from "@/lib/workspace-view-adapter";
import { useCanvasStore } from "@/stores/canvas-store";
import { getCanvasViewportDimensions } from "@/lib/viewport-dimensions";

/** Gap between a source frame and the frame placed beside it. */
const BESIDE_GAP = 40;

function pendingNode(
  id: string,
  frameType: FrameType,
  position: WorkspacePoint,
  size: FrameSize,
  createdAt: string,
): Node<FrameData> {
  return {
    id,
    type: frameType,
    position,
    data: {
      frameId: id,
      frameType,
      title: "",
      content: {},
      status: "idle",
      agentId: null,
      parentFrame: null,
      metadata: {},
      createdAt,
      taskTicket: null,
    },
    style: { width: size.width, height: size.height },
  } as Node<FrameData>;
}

/** Each frame where Tidy puts the newest one, after the frames before it. */
function tidyPositions(
  frameType: FrameType,
  sizes: readonly FrameSize[],
): WorkspacePoint[] {
  const placed: Node<FrameData>[] = [...useCanvasStore.getState().nodes];
  return sizes.map((size) => {
    const id = `pending-${placed.length}`;
    const createdAt = new Date(Date.now() + placed.length).toISOString();
    const update = tidyFramesByTimePage([
      ...placed,
      pendingNode(id, frameType, { x: 0, y: 0 }, size, createdAt),
    ]).find((candidate) => candidate.id === id);
    const position = {
      x: Math.round(update?.x ?? 0),
      y: Math.round(update?.y ?? 0),
    };
    placed.push(pendingNode(id, frameType, position, size, createdAt));
    return position;
  });
}

function besidePosition(sourceFrameId: string | undefined): WorkspacePoint {
  const source = useCanvasStore
    .getState()
    .nodes.find((node) => node.id === sourceFrameId);
  if (!source) return { x: 0, y: 0 };
  return {
    x: source.position.x + frameNodeSize(source).width + BESIDE_GAP,
    y: source.position.y,
  };
}

/** A row from `near`, wrapping below the frames once it leaves the view. */
function nearPositions(
  near: WorkspacePoint,
  sizes: readonly FrameSize[],
): WorkspacePoint[] {
  const { viewport } = useCanvasStore.getState();
  const zoom = Math.max(viewport.zoom, 0.01);
  const { viewportWidth } = getCanvasViewportDimensions();
  const placed: Node[] = [...useCanvasStore.getState().nodes];
  return sizes.map((size) => {
    const position = nextFramePosition({
      nodes: placed,
      frameSize: size,
      viewportCenter: near,
      viewportWidth: viewportWidth / zoom,
    });
    placed.push({
      id: `pending-${placed.length}`,
      position,
      data: {},
      style: { width: size.width, height: size.height },
    } as Node);
    return position;
  });
}

/** The flow point at the centre of the canvas viewport. */
export function canvasViewCenter(): WorkspacePoint {
  const { viewport } = useCanvasStore.getState();
  const zoom = Math.max(viewport.zoom, 0.01);
  const { viewportWidth, viewportHeight } = getCanvasViewportDimensions();
  return {
    x: (-viewport.x + viewportWidth / 2) / zoom,
    y: (-viewport.y + viewportHeight / 2) / zoom,
  };
}

export function canvasFramePositions(
  request: FramePlacementRequest,
): WorkspacePoint[] {
  switch (request.kind) {
    case "tidy":
      return tidyPositions(request.frameType, request.sizes);
    case "beside":
      return [besidePosition(request.sourceFrameId)];
    case "near":
      return nearPositions(request.near ?? canvasViewCenter(), request.sizes);
  }
}
